<script lang="ts">
import { computed, inject, provide, ref } from 'vue';

import { getFocusableElements } from '@sg-core/resources/assets/js/utils';
import { useCollapse } from '@sg-core/src/View/Components/Atoms/Collapse';

import { PhxsgAccordionKey } from './phxsg-accordion.vue';

import type { InjectionKey, Ref } from 'vue';

export interface PhxsgAccordionItemContext {
  id: Ref<string>;
  contentRef: Ref<HTMLElement | null>;
  triggerRef: Ref<HTMLElement | null>;
}

export const PhxsgAccordionItemKey: InjectionKey<PhxsgAccordionItemContext> = Symbol('PhxsgAccordionItem');

export interface PhxsgAccordionItemProps {
  id: string;
}
</script>

<script setup lang="ts">
const props = defineProps<PhxsgAccordionItemProps>();

const accordionContext = inject(PhxsgAccordionKey);

if (!accordionContext) {
  throw new Error('PhxsgAccordionItem must be used inside a PhxsgAccordion');
}

const accordion = accordionContext;

const isOpen = computed(() => accordion.isOpen(props.id));
const contentRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);

useCollapse(contentRef, isOpen);

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    accordion.close(props.id);
    triggerRef.value?.focus();
    return;
  }

  if (event.key !== 'Tab' || !contentRef.value) {
    return;
  }

  const focusable = getFocusableElements(contentRef.value);

  if (event.target === triggerRef.value) {
    if (event.shiftKey || focusable.length === 0) {
      accordion.close(props.id);
    } else if (isOpen.value) {
      event.preventDefault();
      focusable[0].focus();
    }
    return;
  }

  if (
    (event.shiftKey && event.target === focusable[0]) ||
    (!event.shiftKey && event.target === focusable[focusable.length - 1])
  ) {
    accordion.close(props.id);
  }
}

provide(PhxsgAccordionItemKey, {
  id: computed(() => props.id),
  contentRef,
  triggerRef,
});
</script>

<template>
  <li :id="props.id" class="phxsg-accordion__item" @keydown="handleKeydown">
    <slot />
  </li>
</template>
